import { StudioSystemType } from 'types/global'
import { MIN_GROUND_CLEARANCE_MM, SolarPortConfig, SolarPortRelativeTilt } from './SolarPortTypes'

const INPUT_WARNING_KEY = 'SOLARPORTGROUNDMOUNT_INPUT_WARNING'
const PLUGIN_WARNING_KEY = 'SOLARPORTGROUNDMOUNT_PLUGIN_WARNING'

type TranslateFn = (key: string, opts?: Record<string, string>) => string

export function checkSolarPortCompatibility(system: StudioSystemType, translate: TranslateFn): boolean {
  const spIntegrationData = system.integration_json?.solarPortGroundMount
  const groundSlope = spIntegrationData?.groundSlope as number | undefined

  if (groundSlope !== undefined && groundSlope > 6 && spIntegrationData?.postType === 'twinPost') {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: translate('Slope exceeds maximum value of 6 degrees, please adjust the Ground slope input field'),
      key: INPUT_WARNING_KEY,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  const snowZone = spIntegrationData?.snowZone as number | undefined
  if (snowZone !== undefined && snowZone === 6.5) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: translate('The mounting system is incompatible in this snow zone. Contact Solarport'),
      key: INPUT_WARNING_KEY,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  const hasIncompatiblePanelHeight = system.moduleGrids().some((mg) => {
    const panelHeightMm = (mg.moduleType()?.height ?? 0) * 1000
    return panelHeightMm < 1650 || panelHeightMm > 2470
  })
  if (hasIncompatiblePanelHeight) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: translate(
        'Solarport is only compatible with panels with height of at least 1650 mm and at most 2470 mm'
      ),
      key: PLUGIN_WARNING_KEY,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  const hasIncompatiblePanelThickness = system.moduleGrids().some((mg) => {
    const panelThickness = mg.moduleType()?.thickness ?? 0
    return panelThickness !== 30 && panelThickness !== 35
  })
  if (hasIncompatiblePanelThickness) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: translate('Solarport is only compatible with panels with thickness of 30mm or 35mm'),
      key: PLUGIN_WARNING_KEY,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  // --- Non-blocking checks (warnings only, BOM still generated) ---

  const arraySpacing = spIntegrationData?.arraySpacing as number | undefined
  const portraitConfig = (spIntegrationData?.portraitConfig as string) ?? '1inP'
  const minArraySpacing = portraitConfig === '2inP' ? 5000 : 4000
  const minArraySpacingMetres = minArraySpacing / 1000
  if (arraySpacing !== undefined && arraySpacing < minArraySpacing && system.moduleGrids().length >= 2) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: translate('Panels will most likely be shaded by other array, minimum should be %{min}m', {
        min: String(minArraySpacingMetres),
      }),
      key: PLUGIN_WARNING_KEY,
      severity: 'info',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  }

  const spPortraitConfig = (spIntegrationData?.portraitConfig as SolarPortConfig) ?? '1inP'
  const spRelativeTilt = (spIntegrationData?.relativeTilt as SolarPortRelativeTilt) ?? 30
  const minClearanceMm = MIN_GROUND_CLEARANCE_MM[spPortraitConfig]?.[spRelativeTilt]
  if (minClearanceMm) {
    const minClearanceMeters = minClearanceMm / 1000
    const hasInsufficientClearance = system.moduleGrids().some((mg) => {
      const clearance = typeof mg.getGroundClearance === 'function' ? mg.getGroundClearance() : 0
      return clearance < minClearanceMeters - 0.001
    })
    if (hasInsufficientClearance) {
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message: translate(
          'Solarport requires at least %{value}mm ground clearance for %{config} at %{tilt}° tilt',
          {
            value: String(minClearanceMm),
            config: spPortraitConfig === '2inP' ? '2 in Portrait' : '1 in Portrait',
            tilt: String(spRelativeTilt),
          }
        ),
        key: PLUGIN_WARNING_KEY,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  return true
}
